import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useI18n } from 'hooks/usei18n';
import {
  getLegalContent,
  getLegalDocument,
  type ILegalTable,
  type LegalBlock,
  type LegalDocumentKind,
  type LegalRun
} from 'utils/legalDocuments';
import './LegalDocument.scss';

const MIN_HEADINGS_FOR_CONTENTS = 5;
const TOP_ID = 'legal-top';

interface ILegalLayoutProps {
  title: string
  description: string
  subtitle?: string | null
  pdf?: { url: string, fileName: string }
  contents?: Array<{ id: string, text: string }>
  children: ReactNode
}

const renderRuns = (runs: LegalRun[]) => runs.map((run, index) => {
  if (typeof run === 'string') return <Fragment key={index}>{run}</Fragment>;
  if (run.b !== undefined) return run.i ? <strong key={index}><em>{run.b}</em></strong> : <strong key={index}>{run.b}</strong>;
  return <em key={index}>{run.i}</em>;
});

/** Shared frame of every legal page: heading, PDF buttons, table of contents and the readable column. */
export const LegalLayout = ({ title, description, subtitle, pdf, contents = [], children }: ILegalLayoutProps) => {
  const { t } = useTranslation();
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Page scroll is on the body here, so watch the heading instead of window.scrollY.
  useEffect(() => {
    const heading = document.getElementById(TOP_ID);
    if (!heading || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      setShowBackToTop(!entry.isIntersecting);
    });
    observer.observe(heading);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{`${title} | 2kiddo`}</title>
          <meta name="description" content={description}/>
        </Helmet>
      </HelmetProvider>
      <article className="legal-document">
        <h1 className="legal-document-title" id={TOP_ID}>{title}</h1>
        {subtitle && <p className="legal-document-subtitle">{subtitle}</p>}
        {pdf && (
          <div className="flex legal-document-actions">
            <a className="legal-document-button" href={pdf.url} download={pdf.fileName}>
              {t('legalDocument.download')}
            </a>
            <a className="legal-document-button legal-document-button-outline" href={pdf.url} target="_blank" rel="noreferrer">
              {t('legalDocument.open')}
            </a>
          </div>
        )}
        {contents.length >= MIN_HEADINGS_FOR_CONTENTS && (
          <nav className="legal-document-contents" aria-label={t('legalDocument.contents')}>
            <p className="legal-document-contents-title">{t('legalDocument.contents')}</p>
            <ol>
              {contents.map(({ id, text }) => <li key={id}><a href={`#${id}`}>{text}</a></li>)}
            </ol>
          </nav>
        )}
        <div className="legal-document-body">{children}</div>
        {showBackToTop && (
          <a className="legal-document-top" href={`#${TOP_ID}`}>{t('legalDocument.backToTop')}</a>
        )}
      </article>
    </>
  );
};

const renderBlock = (block: LegalBlock, index: number) => {
  switch (block.t) {
    case 'h': {
      const Heading = block.l === 3 ? 'h3' : 'h2';
      return <Heading key={index} id={`section-${index}`}>{block.x}</Heading>;
    }
    case 'p':
      return <p key={index}>{renderRuns(block.r)}</p>;
    case 'ul':
      return <ul key={index}>{block.items.map((item, i) => <li key={i}>{renderRuns(item)}</li>)}</ul>;
    case 'ml':
      return (
        <div key={index} className="legal-document-clauses">
          {block.items.map((item, i) => <p key={i}>{renderRuns(item)}</p>)}
        </div>
      );
  }
};

const renderTable = (table: ILegalTable, index: number) => (
  <table key={index} className="legal-document-table">
    <caption>{table.caption}</caption>
    <thead>
      <tr>{table.head.map((cell, i) => <th key={i} scope="col">{cell}</th>)}</tr>
    </thead>
    <tbody>
      {table.rows.map((row, r) => (
        <tr key={r}>
          {row.map((cell, c) => (c === 0 ? <th key={c} scope="row">{cell}</th> : <td key={c}>{cell}</td>))}
        </tr>
      ))}
    </tbody>
  </table>
);

interface ILegalDocumentProps {
  kind: LegalDocumentKind
}

/** A document that is published as a PDF, shown as a regular page of the site with the PDF as a download. */
const LegalDocument = ({ kind }: ILegalDocumentProps) => {
  const { t } = useTranslation();
  const { language } = useI18n();
  const pdf = getLegalDocument(kind, language);
  const content = getLegalContent(kind, language);
  const blocks = content.blocks ?? [];
  const contents = blocks
    .map((block, index) => ({ block, index }))
    .filter(({ block }) => block.t === 'h' && block.l === 2)
    .map(({ block, index }) => ({ id: `section-${index}`, text: block.t === 'h' ? block.x : '' }));

  return (
    <LegalLayout
      title={t(`footer.${kind}`)}
      subtitle={content.title}
      description={t(`legalDocument.description.${kind}`)}
      pdf={pdf}
      contents={contents}
    >
      {blocks.map(renderBlock)}
      {(content.tables ?? []).map(renderTable)}
    </LegalLayout>
  );
};

export default LegalDocument;
